// src/api/apiClient.ts

import { MOCK_USER } from '../utils/mockUser';
import { trackClientEvent, trackClientException } from '../utils/appInsights';
import { observeProductRequest } from '../utils/productInsights';

// --- KLASY BŁĘDÓW ---
export class ApiError extends Error {
    status: number;
    data: any;
    correlationId?: string;

    constructor(message: string, status: number, data: any, correlationId?: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
        this.correlationId = correlationId;
    }
}

export class NetworkError extends Error {
    correlationId?: string;

    constructor(originalError: any, correlationId?: string) {
        super('Network connection failed');
        this.name = 'NetworkError';
        this.correlationId = correlationId;
        if (originalError instanceof Error) {
            this.stack = originalError.stack;
        }
    }
}

export function createCorrelationId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `fe-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// --- ORYGINALNE I BEZPIECZNE POBIERANIE ADRESU ---
const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL
    : '';

interface ApiClientOptions {
    token?: string | null;
    /** Override generated correlation id (tests / retries). */
    correlationId?: string;
}

const getUrl = (url: string) => {
    if (url.startsWith('/')) {
        return `${API_BASE_URL}${url}`;
    }
    return url;
};

function createHeaders(
    token?: string | null,
    withJsonContentType = false,
    correlationId?: string
): Headers {
    const headers = new Headers();
    // Avoid Content-Type on GET — it forces CORS preflight and is unnecessary.
    if (withJsonContentType) {
        headers.append('Content-Type', 'application/json');
    }

    if (correlationId) {
        headers.append('x-correlation-id', correlationId);
    }

    if (token) {
        if (process.env.NODE_ENV === 'development' && token === 'MOCK_TOKEN') {
            const mockOid = (MOCK_USER.idTokenClaims as { oid?: string } | undefined)?.oid
              || MOCK_USER.localAccountId
              || MOCK_USER.username;
            headers.append('x-test-user-id', mockOid);
        } else {
            headers.append('Authorization', `Bearer ${token}`);
        }
    }
    return headers;
}

function resolveCorrelationId(response: Response, fallback: string, data: any): string {
    const fromHeader =
        response.headers.get('x-correlation-id')
        || response.headers.get('X-Correlation-Id');
    if (fromHeader) return fromHeader;
    if (data && typeof data === 'object' && data.correlationId) {
        return String(data.correlationId);
    }
    return fallback;
}

function reportApiFailure(opts: {
    url: string;
    method: string;
    status?: number;
    correlationId: string;
    kind: 'api' | 'network';
    message?: string;
}) {
    const path = opts.url.replace(/\?.*$/, '').slice(0, 120);
    trackClientEvent('api_client_error', {
        kind: opts.kind,
        method: opts.method,
        path,
        status: opts.status ?? 0,
        correlationId: opts.correlationId,
    });
    if (opts.kind === 'network') {
        trackClientException(new Error(opts.message || 'NetworkError'), {
            source: 'apiClient',
            correlationId: opts.correlationId,
            path,
            method: opts.method,
        });
    }
}

async function handleResponse(response: Response, correlationId: string, method: string, url: string) {
    const contentType = response.headers.get('content-type');
    let data: any = null;

    if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
    } else if (contentType && contentType.includes('application/pdf')) {
        return response;
    } else {
        data = await response.text();
    }

    const cid = resolveCorrelationId(response, correlationId, data);

    if (!response.ok) {
        const errorMessage = (typeof data === 'object' && (data.message || data.error))
            ? (data.message || data.error)
            : (typeof data === 'string' ? data : 'Unknown API Error');

        reportApiFailure({
            url,
            method,
            status: response.status,
            correlationId: cid,
            kind: 'api',
            message: String(errorMessage),
        });
        throw new ApiError(errorMessage, response.status, data, cid);
    }

    return data;
}

async function safeFetch(
    url: string,
    init: RequestInit,
    correlationId: string,
    opts?: { silent?: boolean }
) {
    try {
        return await fetch(url, init);
    } catch (error) {
        if (!opts?.silent) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                reportApiFailure({ url, method: String(init.method || 'GET'), correlationId, kind: 'network', message: 'AbortError' });
                throw new NetworkError(error, correlationId);
            }
            if (error instanceof TypeError && error.message === 'Failed to fetch') {
                reportApiFailure({ url, method: String(init.method || 'GET'), correlationId, kind: 'network', message: 'Failed to fetch' });
                throw new NetworkError(error, correlationId);
            }
        } else if (
            (error instanceof DOMException && error.name === 'AbortError')
            || (error instanceof TypeError && error.message === 'Failed to fetch')
        ) {
            throw new NetworkError(error, correlationId);
        }
        throw error;
    }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * DEV ACA scale-to-zero: poll /api/health until the replica answers.
 * Requires ingress CORS allowedHeaders='*' (App Insights correlation headers).
 * Concurrent callers share one promise so /start + /agentic do not stampede.
 *
 * CWV: never block marketing first paint on this — warm in background;
 * UI may show a fixed (no-CLS) wake strip on app routes only.
 */
let apiReady = false;
let apiReadyPromise: Promise<boolean> | null = null;

export type ApiReadySnapshot = {
    ready: boolean;
    /** Health poll in flight (cold start / first request). */
    warming: boolean;
    /** Last ensureApiReady exhausted attempts without OK. */
    failed: boolean;
};

type ApiReadyListener = (snapshot: ApiReadySnapshot) => void;

let apiReadySnapshot: ApiReadySnapshot = { ready: false, warming: false, failed: false };
const apiReadyListeners = new Set<ApiReadyListener>();

function emitApiReady(next: ApiReadySnapshot) {
    apiReadySnapshot = next;
    apiReadyListeners.forEach((listener) => {
        try {
            listener(next);
        } catch {
            // ignore subscriber errors
        }
    });
}

export function getApiReadySnapshot(): ApiReadySnapshot {
    return apiReadySnapshot;
}

export function subscribeApiReady(listener: ApiReadyListener): () => void {
    apiReadyListeners.add(listener);
    listener(apiReadySnapshot);
    return () => {
        apiReadyListeners.delete(listener);
    };
}

export function resetApiReady() {
    apiReady = false;
    apiReadyPromise = null;
    emitApiReady({ ready: false, warming: false, failed: false });
}

export async function ensureApiReady(options?: {
    attempts?: number;
    delaysMs?: number[];
    timeoutMs?: number;
}): Promise<boolean> {
    if (apiReady) {
        if (!apiReadySnapshot.ready) {
            emitApiReady({ ready: true, warming: false, failed: false });
        }
        return true;
    }
    if (apiReadyPromise) return apiReadyPromise;

    const delays = options?.delaysMs ?? [0, 2000, 4000, 6000, 8000, 10000, 12000, 15000];
    const maxAttempts = options?.attempts ?? delays.length;
    // Cold Functions+Chromium wake can exceed 20s with 0 bytes — do not abort early.
    const timeoutMs = options?.timeoutMs ?? 45000;

    emitApiReady({ ready: false, warming: true, failed: false });

    apiReadyPromise = (async () => {
        for (let i = 0; i < maxAttempts; i += 1) {
            const delay = delays[Math.min(i, delays.length - 1)] || 0;
            if (delay > 0) await sleep(delay);

            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), timeoutMs);
            const correlationId = createCorrelationId();
            try {
                const response = await safeFetch(getUrl('/api/health'), {
                    method: 'GET',
                    headers: createHeaders(null, false, correlationId),
                    credentials: 'omit',
                    signal: controller.signal,
                }, correlationId, { silent: true });
                if (response.ok) {
                    apiReady = true;
                    emitApiReady({ ready: true, warming: false, failed: false });
                    return true;
                }
            } catch {
                // keep polling
            } finally {
                clearTimeout(timer);
            }
        }
        apiReadyPromise = null;
        emitApiReady({ ready: false, warming: false, failed: true });
        return false;
    })();

    return apiReadyPromise;
}

async function withApiReady<T>(run: () => Promise<T>): Promise<T> {
    const ready = await ensureApiReady();
    if (!ready) {
        const correlationId = createCorrelationId();
        reportApiFailure({
            url: '/api/health',
            method: 'GET',
            correlationId,
            kind: 'network',
            message: 'API did not become ready (cold start timeout)',
        });
        throw new NetworkError(new Error('API did not become ready (cold start timeout)'), correlationId);
    }
    try {
        return await run();
    } catch (error) {
        if (error instanceof NetworkError) {
            resetApiReady();
        }
        throw error;
    }
}

export const apiClient = {
    get: async (url: string, options: ApiClientOptions = {}) => {
        return withApiReady(async () => {
            const correlationId = options.correlationId || createCorrelationId();
            const headers = createHeaders(options.token, false, correlationId);
            const response = await safeFetch(getUrl(url), {
                method: 'GET',
                headers,
                credentials: 'include',
            }, correlationId);
            return handleResponse(response, correlationId, 'GET', url);
        });
    },

    post: async (url: string, body: any, options: ApiClientOptions = {}) => {
        return observeProductRequest(url, body, () => withApiReady(async () => {
            const correlationId = options.correlationId || createCorrelationId();
            const headers = createHeaders(options.token, true, correlationId);
            const response = await safeFetch(getUrl(url), {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
                credentials: 'include',
            }, correlationId);
            return handleResponse(response, correlationId, 'POST', url);
        }));
    },

    put: async (url: string, body: any, options: ApiClientOptions = {}) => {
        return withApiReady(async () => {
            const correlationId = options.correlationId || createCorrelationId();
            const headers = createHeaders(options.token, true, correlationId);
            const response = await safeFetch(getUrl(url), {
                method: 'PUT',
                headers,
                body: JSON.stringify(body),
                credentials: 'include',
            }, correlationId);
            return handleResponse(response, correlationId, 'PUT', url);
        });
    },

    delete: async (url: string, options: ApiClientOptions = {}) => {
        return withApiReady(async () => {
            const correlationId = options.correlationId || createCorrelationId();
            const headers = createHeaders(options.token, false, correlationId);
            const response = await safeFetch(getUrl(url), {
                method: 'DELETE',
                headers,
                credentials: 'include',
            }, correlationId);
            return handleResponse(response, correlationId, 'DELETE', url);
        });
    },

    stream: async (url: string, body: any, options: ApiClientOptions = {}) => {
        return observeProductRequest(url, body, () => withApiReady(async () => {
            const correlationId = options.correlationId || createCorrelationId();
            const headers = createHeaders(options.token, true, correlationId);
            const response = await safeFetch(getUrl(url), {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
                credentials: 'include',
            }, correlationId);

            if (!response.ok) {
                const text = await response.text();
                let data: any = text;
                try { data = JSON.parse(text); } catch (e) { /* keep text */ }

                const cid = resolveCorrelationId(response, correlationId, data);
                const errorMessage = (typeof data === 'object' && (data.message || data.error))
                    ? (data.message || data.error)
                    : (typeof data === 'string' ? data : 'Unknown API Error');

                reportApiFailure({
                    url,
                    method: 'POST',
                    status: response.status,
                    correlationId: cid,
                    kind: 'api',
                    message: String(errorMessage),
                });
                throw new ApiError(errorMessage, response.status, data, cid);
            }

            return response;
        }), true);
    }
};
